const CreateCommunicationService = require('services/communication');
const { isAppForeground } = require('utils/app-visibility');

module.exports = initialize;

const internals = { initialized: new WeakMap(), idleMs: 60000 };

function initialize(store) {

    // Native pause/resume already shuts down and rebuilds its transport. Browser
    // tabs keep receiving messages, and check their socket when returning.
    if (typeof document === 'undefined' || window.cordova) {
        return () => {};
    }

    if (internals.initialized.has(store)) {
        return internals.initialized.get(store);
    }

    const { context } = require('app-context');
    const selectors = context.selectors.all;
    let hiddenSince = document.hidden ? Date.now() : null;
    let active = true;
    let pending = null;
    const isEligible = () => active
        && isAppForeground({ selectors, state: store.getState() })
        && selectors.getIsAuthenticated(store.getState())
        && selectors.getVerifiedStatus(store.getState());

    const recover = ({ force }) => {

        if (!isEligible()) {
            return;
        }

        if (pending) {
            return pending;
        }

        const token = selectors.getApiToken(store.getState());
        const communication = CreateCommunicationService.fromContext(context);
        const warn = (error) => {

            if (isEligible() && token === selectors.getApiToken(store.getState())) {
                console.warn('[chat] foreground connection check failed', error);
            }
        };
        const promise = Promise.resolve().then(async () => {

            if (!isEligible() || token !== selectors.getApiToken(store.getState())) {
                return false;
            }

            await communication.ensureConnection({ force });
            return true;
        }).then((ready) => {

            if (!ready || !isEligible() || token !== selectors.getApiToken(store.getState())) {
                return;
            }

            const channelSid = context.handlers.communication.getCurrentChannelSid?.();

            if (channelSid) {
                // Message reads must not keep future socket checks locked.
                Promise.resolve(context.handlers.communication.startListening(channelSid)).catch(warn);
            }
        }).catch(warn).finally(() => {

            if (pending === promise) {
                pending = null;
            }
        });
        pending = promise;
        return promise;
    };
    const onVisibilityChange = () => {

        if (!isAppForeground({ selectors, state: store.getState() })) {
            hiddenSince = hiddenSince ?? Date.now();
            return;
        }

        if (hiddenSince === null) {
            return;
        }

        const force = Date.now() - hiddenSince >= internals.idleMs;
        hiddenSince = null;
        recover({ force });
    };
    const onOnline = () => recover({ force: true });
    const cleanup = () => {

        active = false;
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('online', onOnline);
        internals.initialized.delete(store);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);
    internals.initialized.set(store, cleanup);
    return cleanup;
}
